import React from "react";
import _ from "lodash/fp";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";

import { ControlledFormCheckbox } from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";

import { onDragEnd } from "../../../../services/utils";

const DynamicOrderContentTagDialog = ({
  blocks = [],
  create,
  items = [],
  onClose,
  openState: [open, setOpen],
  readonly,
  tag = {},
  type,
}) => {
  const { range, ...formValues } = tag;

  const renderDraggable = useDraggableInPortal();

  const { control, getValues, setValue, watch } = useForm({
    defaultValues: {
      ...formValues,
      items: items.map(({ id }) => (tag.items.includes(id) ? id : false)),
    },
  });
  const [order, selectedItems] = watch(["order", "items"]);

  const handleCancel = () => {
    setOpen(false);
  };
  const handleClose = () => {
    onClose(create)({
      ...getValues(),
      items: selectedItems.filter(Boolean),
    });
    setOpen(false);
  };
  const move = (sourceIndex, destinationIndex) => {
    const arr = [...order];
    arr.splice(destinationIndex, 0, ...arr.splice(sourceIndex, 1));
    setValue("order", arr);
  };

  return (
    <Dialog maxWidth="md" onClose={handleClose} open={open}>
      <DialogTitle>
        {(readonly && "View") || (create ? "Create" : "Update")} Reorder Content
        Tag
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "row",
          maxHeight: "450px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minWidth: "15em",
            overflowY: "auto",
          }}
        >
          <AppBar color="transparent" elevation={1} position="static">
            <Typography variant="h5">Items</Typography>
          </AppBar>
          <List sx={{ overflowY: "auto" }}>
            {items.map(({ id, name }, index) => (
              <ListItem dense key={id}>
                <ListItemIcon>
                  <ControlledFormCheckbox
                    checkboxProps={{ size: "small" }}
                    control={control}
                    defaultValue={tag?.items?.includes(id) ? id : false}
                    id={`items.${index}`}
                    label=""
                    name={`items.${index}`}
                    readonly={readonly}
                    value={id}
                  />
                </ListItemIcon>
                <ListItemText id={id} primary={name} />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginLeft: 2,
            overflowY: "auto",
          }}
        >
          <AppBar
            color="transparent"
            elevation={1}
            position="static"
            sx={{ marginBottom: 2 }}
          >
            <Typography variant="h5">{_.capitalize(type)}s</Typography>
          </AppBar>
          <DragDropContext onDragEnd={onDragEnd(move)}>
            <Droppable droppableId={`blockOrder`}>
              {(droppableProvided) => (
                <Grid
                  container
                  direction="column"
                  spacing={2}
                  wrap="nowrap"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {order.map((id, index) => (
                    <Draggable
                      draggableId={id}
                      index={index}
                      isDragDisabled={readonly}
                      key={id}
                    >
                      {renderDraggable((draggableProvided) => (
                        <Grid
                          item
                          ref={draggableProvided.innerRef}
                          xs
                          zeroMinWidth
                          {...draggableProvided.draggableProps}
                        >
                          <Paper
                            sx={{
                              backgroundColor: "#f3f3f3",
                              border: "1px solid #0088cc",
                              borderRadius: "15px",
                              MozBorderRadius: "15px",
                              padding: 1,
                              WebkitBorderRadius: "15px",
                            }}
                            {...draggableProvided.dragHandleProps}
                          >
                            <Typography
                              noWrap
                              sx={{ lineHeight: "1.3em" }}
                              variant="body2"
                            >
                              {blocks
                                .filter((o) => o.id === id)
                                .map(({ element, range }) =>
                                  range ? range.toString() : element.textContent
                                )}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Draggable>
                  ))}
                </Grid>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </DialogContent>
      <DialogActions>
        {readonly && (
          <Button onClick={handleCancel} variant="text">
            Close
          </Button>
        )}
        {!readonly && (
          <>
            <Button onClick={handleCancel} variant="text">
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!selectedItems.filter(Boolean).length}
              onClick={handleClose}
              variant="contained"
            >
              {create ? "Create" : "Update"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export { DynamicOrderContentTagDialog };
